var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classNames } from '@chbphone55/classnames';
import { i18n } from '@lingui/core';
import { collapse, expand } from 'element-collapse';
import { css, html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import '../icon/icon.js';
import { activateI18n, detectLocale } from '../i18n';
import { reset } from '../styles';
import { messages as daMessages } from './locales/da/messages.mjs';
import { messages as enMessages } from './locales/en/messages.mjs';
import { messages as fiMessages } from './locales/fi/messages.mjs';
import { messages as nbMessages } from './locales/nb/messages.mjs';
import { messages as svMessages } from './locales/sv/messages.mjs';
import { styles } from './styles';
export { removeToast, toast, updateToast } from './api.js';
const ccToast = {
    wrapper: 'relative overflow-hidden w-full',
    base: 'flex group p-8 mt-16 rounded-8 border-2 pointer-events-auto transition-all',
    positive: 's-bg-positive-subtle s-border-positive-subtle s-text',
    warning: 's-bg-warning-subtle s-border-warning-subtle s-text',
    negative: 's-bg-negative-subtle s-border-negative-subtle s-text',
    iconBase: 'shrink-0 rounded-full w-[16px] h-[16px] m-[8px]',
    iconPositive: 's-icon-positive',
    iconWarning: 's-icon-warning',
    iconNegative: 's-icon-negative',
    iconLoading: 'animate-bounce',
    content: 'self-center mr-8 py-4 last-child:mb-0',
    close: 'bg-transparent ml-auto p-[8px] s-icon hover:s-icon-hover active:s-icon-active',
};
/**
 * You should probably not include this component in HTML markup.
 *
 * As long as the element definition is imported in your application you can use the toast JS API to create toasts.
 *
 * ```js
 * import from '@warp-ds/elements/components/toast';
 * import { toast, removeToast, updateToast } from '@warp-ds/elements/toast';
 * ```
 *
 * [See Storybook for usage examples](https://warp-ds.github.io/elements/?path=/docs/overlays-toast--docs)
 *
 * @internal
 */
export class WarpToast extends LitElement {
    static { this.styles = [
        reset,
        styles,
        css `
      :host {
        display: block;
      }
    `,
    ]; }
    constructor() {
        super();
        this.text = '';
        this.canclose = false;
        this._expanded = false;
        activateI18n(enMessages, nbMessages, fiMessages, daMessages, svMessages);
        // Generate unique ID in constructor to avoid hydration mismatch from reflected property default
        if (!this.id) {
            this.id = Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
        }
    }
    connectedCallback() {
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
    }
    updated() {
        if (!this._expanded && this._wrapper)
            expand(this._wrapper, () => (this._expanded = true));
    }
    get #primaryClasses() {
        const type = this.type || 'success';
        return classNames([
            ccToast.base,
            type === 'success' && ccToast.positive,
            type === 'warning' && ccToast.warning,
            type === 'error' && ccToast.negative,
        ]);
    }
    get #iconClasses() {
        const type = this.type || 'success';
        return classNames([
            ccToast.iconBase,
            type === 'success' && ccToast.iconPositive,
            type === 'warning' && ccToast.iconWarning,
            type === 'error' && ccToast.iconNegative,
        ]);
    }
    /** @internal */
    get _wrapper() {
        return this.renderRoot?.querySelector('section') ?? null;
    }
    /** @internal */
    get _warning() {
        return (this.type || 'success') === 'warning';
    }
    /** @internal */
    get _error() {
        return (this.type || 'success') === 'error';
    }
    /** @internal */
    get _role() {
        return this._error || this._warning ? 'alert' : 'status';
    }
    /** @internal */
    get _typeLabel() {
        if (this._warning) {
            return i18n._({
                id: 'toast.aria.warning',
                message: 'Warning',
                comment: 'Default screenreader message for warning in toast component',
            });
        }
        if (this._error) {
            return i18n._({
                id: 'toast.aria.error',
                message: 'Error',
                comment: 'Default screenreader message for error in toast component',
            });
        }
        return i18n._({
            id: 'toast.aria.successful',
            message: 'Successful',
            comment: 'Default screenreader message for successful in toast component',
        });
    }
    /** @internal */
    get _iconMarkup() {
        const locale = detectLocale();
        if (this._warning)
            return html `<w-icon name="Warning" size="small" locale="${locale}" class="flex"></w-icon>`;
        if (this._error)
            return html `<w-icon name="Error" size="small" locale="${locale}" class="flex"></w-icon>`;
        return html `<w-icon name="Success" size="small" locale="${locale}" class="flex"></w-icon>`;
    }
    async collapse() {
        return new Promise((resolve) => {
            if (this._expanded && this._wrapper) {
                collapse(this._wrapper, resolve);
            }
            else {
                resolve();
            }
        });
    }
    close() {
        const event = new CustomEvent('close', {
            detail: { id: this.id },
            bubbles: true,
            composed: true,
        });
        this.updateComplete.then(() => this.dispatchEvent(event));
    }
    render() {
        if (!this.text)
            return html ``;
        return html ` <section class="${ccToast.wrapper}" aria-label="${this._typeLabel}">
      <div class="${this.#primaryClasses}">
        <div class="${this.#iconClasses}">${this._iconMarkup}</div>
        <div role="${this._role}" class="${ccToast.content}">
          <p>${this.text}</p>
        </div>
        ${when(this.canclose === true, () => html `
            <w-button variant="pill" small="" @click="${this.close}">
              <w-icon name="Close" size="small" locale="${detectLocale()}" style="display: flex" class="flex"></w-icon>
            </w-button>
          `)}
      </div>
    </section>`;
    }
}
__decorate([
    property({ type: String, attribute: true, reflect: true })
], WarpToast.prototype, "id", void 0);
__decorate([
    property({ type: String, attribute: true, reflect: true })
], WarpToast.prototype, "type", void 0);
__decorate([
    property({ type: String, attribute: true, reflect: true })
], WarpToast.prototype, "text", void 0);
__decorate([
    property({ type: Boolean, attribute: true, reflect: true })
], WarpToast.prototype, "canclose", void 0);
__decorate([
    state()
], WarpToast.prototype, "_expanded", void 0);
if (!customElements.get('w-toast')) {
    customElements.define('w-toast', WarpToast);
}
//# sourceMappingURL=toast.js.map